import React from "react";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import Alert from "@material-ui/lab/Alert";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineOutlinedIcon from "@material-ui/icons/DeleteOutlineOutlined";
import { makeStyles, type Theme } from "@material-ui/core/styles";

import NumberField from "src/components/NumberField";
import GLAccountAutocomplete from "src/components/GLAccountAutocomplete";
import { formatDollars } from "src/utils/formatter";
import type { GeneralLedgerAccountV2 } from "src/interfaces/generalLedgerAccount.interface";

export interface SplitRowState {
  key: string;
  id?: string;
  amount: number | "";
  gl_account_id?: string;
  account?: string;
  description?: string;
}

export interface SplitSectionProps {
  rows: SplitRowState[];
  onChangeRows: (rows: SplitRowState[]) => void;
  total: number | "";
  onChangeTotal: (value: number | "") => void;
  disabled?: boolean;
  errors?: { rowAmount?: boolean[]; rowAccount?: boolean[]; total?: string };
  /**
   * Called when the user deletes the second-to-last row, collapsing the
   * split back to a single line. The surviving row is passed so the parent
   * can pre-fill the normal single-transaction form.
   */
  onCollapseToSingle?: (remainingRow: SplitRowState) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 0),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(0.5),
  },
  headerLabel: {
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.secondary,
  },
  line: {
    display: "flex",
    alignItems: "center",
    paddingBottom: theme.spacing(1),
    marginBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  lineContent: {
    flexGrow: 1,
    minWidth: 0,
  },
  topRow: {
    display: "flex",
    alignItems: "flex-start",
  },
  amountField: {
    flexBasis: "35%",
    flexShrink: 0,
    marginRight: theme.spacing(1),
  },
  glField: {
    flexGrow: 1,
  },
  actionCol: {
    width: 40,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  descRow: {
    display: "flex",
  },
  descField: {
    flexGrow: 1,
  },
  deleteButton: {
    color: theme.palette.error.main,
  },
  addButton: {
    color: theme.palette.primary.main,
  },
  totalsRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: theme.spacing(1),
  },
  totalInput: {
    marginBottom: theme.spacing(1),
  },
  sumText: {
    fontWeight: 500,
  },
  alert: {
    marginTop: theme.spacing(1),
  },
}));

function genKey(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function isAllowedForSplit(account: GeneralLedgerAccountV2): boolean {
  if (!account) return false;
  if (account.id === "transfers") return false;
  // Bank account / cash and credit card accounts can't be a split line; those
  // belong to the funding side of the transaction, not the categorization.
  if (account.type?.[1] === "bank-account-cash") return false;
  if (account.type?.[1] === "credit-card") return false;
  return true;
}

export function SplitSection({
  rows,
  onChangeRows,
  total,
  onChangeTotal,
  disabled,
  errors,
  onCollapseToSingle,
}: SplitSectionProps) {
  const classes = useStyles();

  const handleRowAmountChange = (index: number, value: number | undefined) => {
    const next = [...rows];
    next[index] = {
      ...next[index],
      amount: value == null ? "" : value,
    };
    onChangeRows(next);
  };

  const handleRowAccountChange = (
    index: number,
    account: GeneralLedgerAccountV2 | null
  ) => {
    const next = [...rows];
    next[index] = {
      ...next[index],
      gl_account_id: account?.id,
      account: account?.name,
    };
    onChangeRows(next);
  };

  const handleRowDescriptionChange = (index: number, value: string) => {
    const next = [...rows];
    next[index] = {
      ...next[index],
      description: value,
    };
    onChangeRows(next);
  };

  const handleAddRow = () => {
    onChangeRows([
      ...rows,
      {
        key: genKey(),
        amount: "",
        gl_account_id: undefined,
        account: undefined,
      },
    ]);
  };

  const handleRemoveRow = (index: number) => {
    if (rows.length <= 1) return;
    const next = rows.filter((_, i) => i !== index);
    if (next.length === 1 && onCollapseToSingle) {
      onCollapseToSingle(next[0]);
      return;
    }
    onChangeRows(next);
  };

  const sum = rows.reduce(
    (acc, r) => acc + (typeof r.amount === "number" ? r.amount : 0),
    0
  );
  const totalNum = typeof total === "number" ? total : 0;
  const difference = totalNum - sum;
  const balanced = Math.abs(difference) < 0.005;

  return (
    <Box className={classes.root}>
      <Typography variant="subtitle1" className={classes.sectionTitle}>
        Transaction Accounts
      </Typography>
      <NumberField
        className={classes.totalInput}
        fullWidth
        variant="outlined"
        margin="dense"
        label="Total Amount"
        disabled={disabled}
        allowNegative
        value={typeof total === "number" ? total : ""}
        onChange={(event) => {
          const value = event.target.value;
          onChangeTotal(value == null ? "" : value);
        }}
        InputProps={{
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        }}
        fixed={2}
        error={!!errors?.total}
        helperText={errors?.total}
      />
      <Box className={classes.headerRow}>
        <Box className={classes.lineContent}>
          <Box className={classes.topRow}>
            <Typography
              variant="body2"
              className={`${classes.amountField} ${classes.headerLabel}`}
            >
              Amount
            </Typography>
            <Typography
              variant="body2"
              className={`${classes.glField} ${classes.headerLabel}`}
            >
              General Ledger Account
            </Typography>
          </Box>
        </Box>
        <Box className={classes.actionCol} />
      </Box>
      {rows.map((row, i) => (
        <Box key={row.key} className={classes.line}>
          <Box className={classes.lineContent}>
            <Box className={classes.topRow}>
              <Box className={classes.amountField}>
                <NumberField
                  fullWidth
                  variant="outlined"
                  margin="dense"
                  disabled={disabled}
                  allowNegative
                  value={typeof row.amount === "number" ? row.amount : ""}
                  onChange={(event) =>
                    handleRowAmountChange(i, event.target.value)
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  fixed={2}
                  error={errors?.rowAmount?.[i]}
                />
              </Box>
              <Box className={classes.glField}>
                <GLAccountAutocomplete
                  label=""
                  disabled={disabled}
                  value={row.gl_account_id || row.account || null}
                  onChange={(account) => handleRowAccountChange(i, account)}
                  filter={isAllowedForSplit}
                  error={errors?.rowAccount?.[i]}
                />
              </Box>
            </Box>
            <Box className={classes.descRow}>
              <Box className={classes.descField}>
                <TextField
                  fullWidth
                  variant="outlined"
                  margin="dense"
                  label="Description"
                  disabled={disabled}
                  value={row.description ?? ""}
                  onChange={(event) =>
                    handleRowDescriptionChange(i, event.target.value)
                  }
                />
              </Box>
            </Box>
          </Box>
          <Box className={classes.actionCol}>
            <Tooltip title="Remove line">
              <span>
                <IconButton
                  size="small"
                  disabled={disabled || rows.length <= 1}
                  onClick={() => handleRemoveRow(i)}
                  className={classes.deleteButton}
                >
                  <DeleteOutlineOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      ))}

      <Box className={classes.totalsRow}>
        <Tooltip title="Add line">
          <span>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={handleAddRow}
              className={classes.addButton}
            >
              <AddIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Typography variant="body2" className={classes.sumText}>
          Sum: {formatDollars(sum)} of {formatDollars(totalNum)}
        </Typography>
      </Box>

      {!balanced && (typeof total === "number" || sum !== 0) ? (
        <Alert severity="error" className={classes.alert}>
          Split amounts must total {formatDollars(totalNum)} (off by{" "}
          {formatDollars(difference)}).
        </Alert>
      ) : null}
    </Box>
  );
}

export function createEmptySplitRow(): SplitRowState {
  return {
    key: genKey(),
    amount: "",
    gl_account_id: undefined,
    account: undefined,
  };
}
